import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import client, { imgUrl, shuffled } from "@/lib/glossyApi";
import MiniAd from "@/components/MiniAd";
import MagazineLayout from "@/components/MagazineLayout";
import { Reveal } from "@/components/Reveal";
import { categoryColor, categoryColorBright } from "@/lib/categoryColors";

// Preferred display order for the category tabs — the Newsroom's nine house categories first
const CATEGORY_ORDER = [
  "Money", "Business", "Culture", "Style", "Trends", "Marketing", "Workplace", "Community", "Success",
  "Sustainability", "Innovation", "Lifestyle", "Startups", "Family", "Crafts", "Trades",
];

export default function ReadPage({ variant = "edit" }) {
  const [params, setParams] = useSearchParams();
  const active = params.get("section") || "All";
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");

  const cfg = variant === "newsroom"
    ? { title: "The Newsroom", kicker: "The Magazine", docTitle: "The Newsroom — S/ME",
        query: "type=editorial&authored_by=magazine",
        intro: <>The S/ME desk on money, culture and the ideas moving UK business. For member cover stories, see <a href="/interviews" className="underline underline-offset-2 hover:text-gloss-gold">The Interviews</a>.</> }
    : { title: "The Edit", kicker: "The Members", docTitle: "The Edit — S/ME",
        query: "type=edit",
        intro: <>Member deep-dives: bold, opinionated stories on the S/ME businesses shaping the UK. For the magazine's own reporting, see <a href="/newsroom" className="underline underline-offset-2 hover:text-gloss-gold">The Newsroom</a>.</> };

  useEffect(() => {
    document.title = cfg.docTitle;
    const url = `/articles?${cfg.query}&limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`;
    client.get(url).then((r) => setArticles(shuffled(r.data))).catch(() => {});
  }, [q, variant]); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = useMemo(() => {
    const present = new Set(articles.map((a) => a.category).filter(Boolean));
    const ordered = CATEGORY_ORDER.filter((c) => present.has(c));
    const extras = [...present].filter((c) => !CATEGORY_ORDER.includes(c)).sort();
    return ["All", ...ordered, ...extras];
  }, [articles]);

  const visible = useMemo(
    () => (active === "All" ? articles : articles.filter((a) => a.category === active)),
    [articles, active]
  );

  return (
    <MagazineLayout>
      <div className="pt-[68px] bg-cream min-h-screen">
        <header className="px-5 sm:px-8 pt-16 sm:pt-24 pb-10 max-w-[1600px] mx-auto">
          <p className="overline text-gloss-silver mb-4">{cfg.kicker}</p>
          <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-none">{cfg.title}</h1>
          <p className="font-sans text-lg text-ink/70 mt-5 max-w-xl">{cfg.intro}</p>

          <div className="mt-10 flex flex-col gap-6">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search stories…"
              className="w-full max-w-md bg-transparent border-b border-ink/30 py-3 font-sans text-lg focus:outline-none focus:border-gloss-silver transition-colors"
              data-testid="article-search"
            />
            <div className="flex flex-wrap gap-3">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setParams(c === "All" ? {} : { section: c })}
                  className={`overline px-4 py-2 border transition-colors flex items-center gap-2 ${active === c ? (c === "All" ? "bg-ink text-cream border-ink" : "text-cream") : "border-ink/30 hover:border-ink"}`}
                  style={active === c && c !== "All" ? { backgroundColor: categoryColor(c), borderColor: categoryColor(c) } : undefined}
                  data-testid={`filter-${c.toLowerCase()}`}
                >
                  {c !== "All" && <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: active === c ? "rgba(245,241,232,0.9)" : categoryColor(c) }} />}
                  {c}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="px-5 sm:px-8 max-w-[1600px] mx-auto mb-10"><MiniAd variant="banner" mode="image" space={variant === "newsroom" ? "newsroom_banner" : "edit_band"} /></div>

        <section className="px-5 sm:px-8 pb-28 max-w-[1600px] mx-auto">
          {visible.length === 0 ? (
            <p className="font-sans text-ink/50 py-20 text-center" data-testid="no-stories">No stories yet in this category.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 border-t border-ink/20 pt-12">
              {visible.map((a, i) => (
                <Reveal key={a.id} delay={(i % 3) * 0.06}>
                  <Link to={`/read/${a.slug}`} className="group block relative overflow-hidden bg-ink" data-testid="article-card">
                    <div className="relative aspect-[4/5]">
                      <img src={imgUrl(a)} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-black/5" />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-6">
                      <p className="overline flex items-center gap-2" style={{ color: categoryColorBright(a.category) }}>
                        <span className="w-5 h-[2px] inline-block" style={{ backgroundColor: categoryColorBright(a.category) }} />
                        {a.category}
                      </p>
                      <h3 className="font-serif text-cream text-2xl sm:text-3xl tracking-tight mt-2 leading-tight">{a.title}</h3>
                      <p className="font-sans text-sm text-cream/75 mt-2.5 leading-relaxed line-clamp-2">{a.dek}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </section>
      </div>
    </MagazineLayout>
  );
}
